import { createSignal, For, Show, type JSX } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import type { ViewProps } from 'wdc-cube-solid'
import type {
    DeviceSourceScope,
    FunnelStepScope,
    KpiScope,
    TrainingTypeScope,
    UserBaseScope
} from 'mormaii-web-presentation/user-base'

import { ComboChart, HorizontalBars } from '../../../components/charts'
import { CompanyPicker } from '../../../components/company-picker'
import { Icon } from '../../../components/icons'
import { DateField, Menu, Panel, Spinner } from '../../../components/ui'
import { formatCount, formatDecimal } from 'mormaii-web-presentation'

import Css from './user-base.module.scss'

/**
 * Adocao e Engajamento.
 *
 * Os seis filtros do topo estao todos no ENDERECO, entao um recorte desta tela - "os
 * ultimos 90 dias da empresa X, com janela de ativo de 7 dias" - e um link que se
 * manda a alguem. No painel antigo eles eram estado de componente, e o F5 os perdia.
 *
 * O scope entrega numeros CRUS nas series (o grafico plota numero) e o TEXTO pronto do
 * resto. A view nao formata nem calcula: a formatacao de leitura e o CSV moram na
 * apresentacao desde 08/10/2026. Os eixos dos graficos recebem a funcao de formatacao
 * do dominio, porque as marcas nascem da geometria daqui.
 */

/**
 * A cor de cada serie, na paleta do app - ver `styles/theme.css`.
 *
 * Em HEXA e nao em `var(--accent)` porque quem as consome sao os graficos, que
 * recebem cor como VALOR (atributo de `<svg>`), e nao como declaracao de estilo.
 *
 * Quatro cores para nove series: elas nunca aparecem juntas. Cada grafico usa duas ou
 * tres, e repetir o acento no que e "o numero principal" de cada um e o que mantem a
 * leitura igual de uma secao para a outra.
 */
const COLORS = {
    connected: '#34E0D2',
    active: '#6FE56A',
    new: '#F2B341',
    cumulative: '#4A9FE7',
    users: '#34E0D2',
    workouts: '#6FE56A',
    total: '#34E0D2',
    average: '#F2B341',
    distance: '#4A9FE7'
}

const ACCENTS: Record<string, string> = {
    indigo: Css.indigo,
    emerald: Css.emerald,
    amber: Css.amber,
    sky: Css.sky,
    rose: Css.rose
}

export function UserBaseView(props: ViewProps<UserBaseScope>): JSX.Element {
    const monthly = () => props.scope.granularity === 'MONTH'

    return (
        <div class={Css.screen} aria-busy={props.scope.loading}>
            <FilterBar scope={props.scope} />

            <Show when={props.scope.error}>
                {(message) => (
                    <p role="alert" class={Css.error}>
                        {message()}
                    </p>
                )}
            </Show>

            <Show when={props.scope.loading && !props.scope.hasOverview}>
                <div class={Css.computing}>
                    <Spinner class={Css.spinner} />
                    Apurando os indicadores da base…
                </div>
            </Show>

            <Show when={props.scope.hasOverview}>
                <Methodology scope={props.scope} />
                <Overview scope={props.scope} monthly={monthly()} />
            </Show>

            <Show when={props.scope.hasTraining}>
                <Section title="Treinos">
                    <Training scope={props.scope} />
                </Section>
            </Show>

            <Show when={props.scope.hasPhysical}>
                <Section title="Performance física">
                    <Physical scope={props.scope} />
                </Section>
            </Show>

            <Show when={props.scope.hasDevices}>
                <Section title="Vestíveis">
                    <Devices scope={props.scope} />
                </Section>
            </Show>
        </div>
    )
}

function Section(props: { title: string; children: JSX.Element }): JSX.Element {
    return (
        <div class={Css.section}>
            <h2 class={Css.sectionTitle}>{props.title}</h2>
            {props.children}
        </div>
    )
}

// ========== A BARRA DE FILTROS ==========

function FilterBar(props: { scope: UserBaseScope }): JSX.Element {
    return (
        <section class={Css.filterBar}>
            <div class={Css.haloTop} />
            <div class={Css.haloBottom} />

            <div class={Css.filterBarBody}>
                <div class={Css.header}>
                    {/*
                        A barra diz O QUE ELA FAZ, e nao o nome da tela - e o mesmo que
                        "Filtrar monitoramentos" e "Filtrar empresas". Ela repetia o titulo
                        da tela e o subtitulo dele, palavra por palavra, vinte pixels abaixo
                        do cabecalho que ja os escreve.
                    */}
                    <div class={Css.titles}>
                        <h2 class={Css.filterBarTitle}>Filtrar indicadores</h2>
                    </div>

                    <div class={Css.commands}>
                        <GlassButton
                            label="Atualizar"
                            disabled={props.scope.loading}
                            onClick={() => void props.scope.onRefresh()}
                        >
                            <Show when={props.scope.loading} fallback={<path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />}>
                                <path d="M21 12a9 9 0 1 1-9-9" class={Css.spinningIcon} />
                            </Show>
                        </GlassButton>

                        {/*
                            O PDF sai pela caixa de impressao do navegador, e nao por
                            uma biblioteca: o que se quer imprimir e exatamente esta
                            tela, com os graficos em SVG - que e o que sai nitido no
                            papel.
                        */}
                        <GlassButton
                            label="PDF"
                            title="Gera o PDF pela caixa de impressão do navegador"
                            onClick={() => window.print()}
                        >
                            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z" />
                        </GlassButton>
                    </div>
                </div>

                {/*
                    O mesmo arranjo do Relatorio de Sono: o seletor de empresa cresce
                    com o espaco que sobra, e periodo e datas ficam com a largura que
                    precisam.
                */}
                <div class={Css.filterBarFields}>
                    <Show when={props.scope.companySelector}>
                        {(selector) => (
                            <CompanyPicker scope={selector()} variant="glass" class={Css.filterBarCompany} />
                        )}
                    </Show>

                    {/*
                        A barra em DOIS domínios, e não num período só.
                        
                        Os indicadores desta tela sao de duas naturezas, e a barra antiga
                        escondia isso: ela anunciava "Periodo: X a Y" e, para quatro dos seis
                        cartoes da visao geral, a data INICIAL nao valia nada - eles cortam no
                        fim (`countUsersUntil`), ou leem uma janela que termina no fim
                        (`fim + 1 - janela`), ou o ULTIMO balde da serie.
                        
                        Agora cada controle diz onde manda: a data de REFERENCIA e a janela
                        governam os retratos; o inicio e a visao governam o fluxo. Sao os
                        mesmos quatro campos de antes - `to`, `activeWindowDays`, `from` e
                        `granularity` -, reagrupados pelo que cada um decide.
                        
                        A referencia E o fim do intervalo, e nao uma terceira data: com duas
                        ancoras independentes a tela admitiria "retrato em setembro, fluxo de
                        janeiro", que o servidor nem sabe responder - ele tem um `endTs` so.
                    */}
                    <Show when={props.scope.period}>
                        {(period) => (
                            <>
                                {/*
                                    Tres eixos, e nao quatro: a JANELA DE ATIVIDADE saiu em
                                    23/09/2026. Ela oferecia 7, 15 ou 30 dias contados da
                                    referencia, e convivia mal com o intervalo - um relatorio
                                    de 90 dias mostrava um cartao escrito "Ativos (30 dias)",
                                    e nada na tela dizia que aqueles 30 nao eram os 90 do
                                    grafico ao lado. Agora "ativo" quer dizer "registrou algo
                                    no intervalo", e o rotulo do cartao acompanha o que se
                                    pediu.
                                    
                                    O atalho vem PRIMEIRO, e com rotulo proprio.
                                    
                                    Ele nao pertence a nenhum dos dois dominios - e o jeito
                                    rapido de pousar as duas datas de uma vez ("ultimos 30
                                    dias" poe a referencia em hoje e o inicio 29 dias atras).
                                    Dentro do grupo de fluxo ele lia mal: "FLUXO DESDE:
                                    Escolher datas" nao e frase.
                                */}
                                <div class={Css.axis}>
                                    <span class={Css.axisLabel}>Intervalo</span>
                                    <Menu
                                        onDark
                                        summary={period().summary}
                                        options={period().options.map((option) => ({
                                            label: option.label,
                                            current: option.current,
                                            onSelect: option.onSelect
                                        }))}
                                    />
                                </div>

                                <div class={Css.axis}>
                                    <span class={Css.axisLabel}>Situação em</span>
                                    <DateField
                                        onDark
                                        hideLabel
                                        label="Situação em"
                                        value={period().to}
                                        min={period().from}
                                        onChange={(value) => period().onToChanged(value)}
                                        class={Css.filterBarDate}
                                    />
                                </div>

                                <div class={Css.axis}>
                                    <span class={Css.axisLabel}>Fluxo desde</span>
                                    <DateField
                                        onDark
                                        hideLabel
                                        label="Fluxo desde"
                                        value={period().from}
                                        max={period().to}
                                        onChange={(value) => period().onFromChanged(value)}
                                        class={Css.filterBarDate}
                                    />
                                </div>
                            </>
                        )}
                    </Show>

                    <div class={Css.axis}>
                        <span class={Css.axisLabel}>Visão</span>
                        <div class={Css.toggle}>
                            <For each={['DAY', 'MONTH'] as const}>
                                {(option) => (
                                    <button
                                        type="button"
                                        onClick={() => props.scope.onGranularityChanged(option)}
                                        class={Css.side}
                                        classList={{ [Css.current]: props.scope.granularity === option }}
                                    >
                                        {option === 'DAY' ? 'Diário' : 'Mensal'}
                                    </button>
                                )}
                            </For>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

function GlassButton(props: {
    label: string
    title?: string
    disabled?: boolean
    onClick: () => void
    children: JSX.Element
}): JSX.Element {
    return (
        <button
            type="button"
            title={props.title}
            disabled={props.disabled}
            onClick={props.onClick}
            class={Css.glassButton}
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={Css.icon}
            >
                {props.children}
            </svg>
            {props.label}
        </button>
    )
}

// ========== A NOTA DE METODOLOGIA ==========

/**
 * Como os numeros sao apurados, recolhida por padrao.
 *
 * Na IMPRESSAO ela abre sozinha (`print:block`): o PDF vai para quem nao esteve na
 * tela, e um numero sem o criterio que o produziu e um numero que se discute sem
 * base.
 */
function Methodology(props: { scope: UserBaseScope }): JSX.Element {
    const [open, setOpen] = createSignal(false)

    return (
        <section class={Css.method}>
            <button type="button" onClick={() => setOpen(!open())} class={Css.methodTrigger}>
                <span class={Css.methodTitle}>
                    <Icon name="alert" size={16} class={Css.methodIcon} />
                    Como estes números são apurados
                </span>
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class={Css.methodArrow}
                    classList={{ [Css.open]: open() }}
                >
                    <path d="m6 9 6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>

            <div class={Css.methodBody} classList={{ [Css.open]: open() }}>
                <p>
                    <strong>Período:</strong> {props.scope.periodStartLabel} a {props.scope.periodEndLabel}, visão{' '}
                    {props.scope.granularityDescription.toLowerCase()}
                    {props.scope.companyName ? `, empresa ${props.scope.companyName}` : ', toda a base'}. Apurado em{' '}
                    {props.scope.generatedAtLabel}.
                </p>
                <p>
                    <strong>Dois tipos de número, e o filtro diz qual é qual:</strong> os de <strong>situação</strong>{' '}
                    são o retrato em {props.scope.periodEndLabel} - a data inicial não entra neles, e trocá-la não muda
                    nenhum. Os de <strong>fluxo</strong> somam o que aconteceu entre {props.scope.periodStartLabel} e{' '}
                    {props.scope.periodEndLabel}. Treinos e performance física são todos de fluxo.
                </p>
                <p>
                    <strong>Colaborador ativo:</strong> registrou medição ou treino dentro do intervalo - de{' '}
                    {props.scope.periodStartLabel} a {props.scope.periodEndLabel}, ou seja{' '}
                    {props.scope.activeWindowDays} dias. Até 23/09/2026 a janela era escolhida à parte (7, 15 ou 30
                    dias) e podia divergir do intervalo do relatório; hoje são a mesma coisa, e é por isso que o número
                    entre parênteses acompanha o que você pediu.
                </p>
                <p>
                    <strong>Pessoa conectada no dia:</strong> colaborador que registrou dados naquele dia. A plataforma
                    não mantém histórico de acessos, então a sincronização do wearable é o vestígio de uso disponível.
                </p>
                <p>
                    <strong>Base considerada:</strong> colaboradores - quem tem ficha e é medido - com cadastro ativo. O
                    total de cadastrados, sem o filtro de ativo, aparece no primeiro indicador.
                </p>
                <p>
                    <strong>Usuário não é colaborador:</strong> usuário é quem tem conta, e entra no painel ou no app;
                    colaborador é quem tem ficha e é medido. Quem administra ou atende é usuário e não é colaborador.{' '}
                    <strong>Todo indicador desta tela conta colaboradores</strong> - quem entra no painel é assunto de
                    "Acesso ao painel", onde a lista é de contas.
                </p>
                <p>
                    <strong>Corte do dia:</strong> os dias seguem o horário de Brasília, o mesmo da agregação diária da
                    plataforma: o que se registra até as 23h59 entra no próprio dia.
                </p>
                <p>
                    <strong>Distância:</strong> vem do wearable. Aparelhos com firmware anterior à correção de
                    comprimento de passada subestimam a distância, então a quilometragem histórica é conservadora.
                </p>
            </div>
        </section>
    )
}

// ========== VISAO GERAL ==========

function Overview(props: { scope: UserBaseScope; monthly: boolean }): JSX.Element {
    const labels = () => props.scope.overviewSeries.map((point) => point.label)

    return (
        <div class={Css.group}>
            {/*
                Os cartoes em DOIS grupos, pelo que cada numero le do filtro.
                
                Sem isto, a barra prometeria dois dominios e a grade continuaria indistinta -
                seis cartoes lado a lado, quatro deles cortando na data de referencia e dois
                somando o intervalo, sem nada os separando. O funil vai com os retratos: os
                quatro degraus sao "ja fez, ate o fim do periodo".
                
                "Ativos" ficou do lado do FLUXO quando a janela passou a ser o intervalo: ele
                responde "entre as duas datas", e nao "na data".
            */}
            <SectionLabel
                title={`Situação em ${props.scope.periodEndLabel}`}
                hint="Retrato na data de referência - a data inicial não entra nestes números."
            />
            <Kpis items={props.scope.situationKpis} columns={4} />

            {/*
                O funil na largura inteira, mas com os marcos LADO A LADO.
                
                Empilhados, as quatro barras se esticavam por mil e cem pixels para dizer
                quatro numeros - muita area horizontal para pouca informacao. Em quatro
                colunas, a mesma largura passa a carregar quatro comparacoes, e a barra volta
                a ter comprimento de barra.
            */}
            <Panel title="Funil de ativação">
                <div class={Css.funnel}>
                    {/*
                        A nota diz de que a porcentagem e, porque `FunnelBar` divide pelo
                        PRIMEIRO degrau e nao pelo anterior. Sem isso, "funil" convida a ler
                        cada barra como subconjunto da de cima - e os marcos nao se encaixam:
                        em QA, 21 das 47 pessoas que sincronizaram nunca preencheram a
                        anamnese. Os numeros estavam certos; a leitura e que nao estava.
                    */}
                    <p class={Css.funnelNote}>
                        Situação da base em {props.scope.periodEndLabel}. Cada barra é a porcentagem do total de
                        cadastrados - são marcos independentes, e não etapas encaixadas.
                    </p>
                    {/*
                        A frase de fecho saiu daqui em 23/09/2026: ela dizia quantos nunca
                        sincronizaram, e esse numero virou CARTAO no grupo de situacao. Repetir
                        seria a mesma informacao duas vezes, e a versao em cartao e a que se
                        compara com os vizinhos.
                    */}

                    <ul class={Css.funnelList}>
                        <For each={props.scope.funnel}>{(step) => <FunnelBar step={step} />}</For>
                    </ul>
                </div>
            </Panel>

            <SectionLabel
                title={`Fluxo de ${props.scope.periodStartLabel} a ${props.scope.periodEndLabel}`}
                hint="O que aconteceu entre as duas datas."
            />
            <Kpis items={props.scope.flowKpis} columns={4} />

            <ChartPanel
                title="Pessoas conectadas por dia"
                description={
                    props.monthly
                        ? 'Colaboradores distintos por mês e a média diária de conectados dentro de cada mês.'
                        : 'Colaboradores distintos que registraram dados no dia, com a curva de ativos na janela escolhida.'
                }
                onExport={() => void props.scope.onExportOverview()}
            >
                <ComboChart
                    height={300}
                    labels={labels()}
                    format={formatCount}
                    series={[
                        {
                            name: props.monthly ? 'Conectados no mês' : 'Conectados',
                            kind: 'area',
                            color: COLORS.connected,
                            values: props.scope.overviewSeries.map((point) => point.connectedUsers)
                        },
                        ...(props.monthly
                            ? [
                                  {
                                      name: 'Média diária no mês',
                                      kind: 'line' as const,
                                      color: COLORS.cumulative,
                                      values: props.scope.overviewSeries.map((point) => point.dailyAverageConnected)
                                  }
                              ]
                            : []),
                        {
                            name: `Ativos (${props.scope.activeWindowDays} dias)`,
                            kind: 'line',
                            color: COLORS.active,
                            values: props.scope.overviewSeries.map((point) => point.activeUsersInWindow)
                        }
                    ]}
                />
            </ChartPanel>

            {/*
                Largura inteira, como o grafico acima.
                
                Ele dividia a linha com o "Funil de ativacao" numa grade de tercos. O funil
                subiu para o grupo de RETRATO, que e o que ele e - "Situacao da base em ..." ja
                estava escrito na nota dele -, e deixar um grafico sozinho numa grade de tercos
                so o espremeria em dois tercos sem razao.
            */}
            <ChartPanel
                title="Evolução da base"
                description="Novos cadastros no período e o total acumulado de colaboradores."
                onExport={() => void props.scope.onExportOverview()}
            >
                <ComboChart
                    height={280}
                    labels={labels()}
                    format={formatCount}
                    series={[
                        {
                            name: 'Novos cadastros',
                            kind: 'bar',
                            color: COLORS.new,
                            values: props.scope.overviewSeries.map((point) => point.newUsers)
                        },
                        {
                            name: 'Total acumulado',
                            kind: 'line',
                            color: COLORS.cumulative,
                            values: props.scope.overviewSeries.map((point) => point.cumulativeUsers)
                        }
                    ]}
                />
            </ChartPanel>
        </div>
    )
}

function FunnelBar(props: { step: FunnelStepScope }): JSX.Element {
    return (
        <li class={Css.rung}>
            {/*
                So o degrau que a lista sabe reproduzir vira botao - hoje "Já
                registraram treino". Os outros tres nao tem filtro que os repita:
                "Preencheram a anamnese" e um campo da ficha, e a lista nao filtra por
                ele; um caminho que trouxesse outro conjunto seria pior que nenhum.
            */}
            <Dynamic
                component={props.step.onOpen ? 'button' : 'div'}
                type={props.step.onOpen ? 'button' : undefined}
                class={Css.rungTexts}
                classList={{ [Css.rungOpens]: !!props.step.onOpen }}
                title={props.step.onOpen ? 'Ver estes colaboradores' : undefined}
                onClick={props.step.onOpen ? () => void props.step.onOpen?.() : undefined}
            >
                <span class={Css.rungLabel}>{props.step.label}</span>
                <span class={Css.rungValue}>
                    {props.step.valueLabel}
                    <span class={Css.rungPercent}>({props.step.shareLabel})</span>
                </span>
            </Dynamic>
            <div class={Css.track}>
                <div class={Css.filled} style={{ width: `${props.step.barWidth}%` }} />
            </div>
        </li>
    )
}

// ========== TREINOS ==========

function Training(props: { scope: UserBaseScope }): JSX.Element {
    return (
        <div class={Css.group}>
            <Kpis items={props.scope.trainingKpis} columns={4} />

            <div class={Css.halvesGrid}>
                <ChartPanel
                    title="Colaboradores por modalidade"
                    description="Treinos disponíveis que foram efetivamente ativados no período."
                    onExport={() => void props.scope.onExportTrainingTypes()}
                >
                    <HorizontalBars
                        format={formatCount}
                        rows={props.scope.trainingTypes.map((kind) => ({
                            label: kind.label,
                            values: [
                                { name: 'Colaboradores únicos', value: kind.users, color: COLORS.users },
                                { name: 'Treinos', value: kind.workouts, color: COLORS.workouts }
                            ]
                        }))}
                    />
                </ChartPanel>

                <ChartPanel
                    title="Treinos ao longo do tempo"
                    description="Sessões registradas e colaboradores distintos por período."
                    onExport={() => void props.scope.onExportTrainingSeries()}
                >
                    <ComboChart
                        height={260}
                        format={formatCount}
                        labels={props.scope.trainingSeries.map((point) => point.label)}
                        series={[
                            {
                                name: 'Treinos',
                                kind: 'bar',
                                color: COLORS.workouts,
                                values: props.scope.trainingSeries.map((point) => point.workouts)
                            },
                            {
                                name: 'Colaboradores únicos',
                                kind: 'line',
                                color: COLORS.users,
                                values: props.scope.trainingSeries.map((point) => point.users)
                            }
                        ]}
                    />
                </ChartPanel>
            </div>

            <Panel title="Detalhe por modalidade" flush>
                <div class={Css.scroller}>
                    <table class={Css.table}>
                        <thead>
                            <tr class={Css.tableHead}>
                                <th class={Css.textColumn}>Modalidade</th>
                                <th class={Css.numberColumn}>Treinos</th>
                                <th class={Css.numberColumn}>Colaboradores</th>
                                <th class={Css.numberColumn}>Treinos/colaborador</th>
                                <th class={Css.numberColumn}>Duração total</th>
                                <th class={Css.numberColumn}>Distância</th>
                                <th class={Css.numberColumn}>FC média</th>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={props.scope.trainingTypes}>{(kind) => <TrainingRow scope={kind} />}</For>
                        </tbody>
                    </table>
                </div>

                <Show when={props.scope.trainingTypes.length === 0}>
                    <p class={Css.noRow}>Nenhum treino registrado no período.</p>
                </Show>
            </Panel>
        </div>
    )
}

function TrainingRow(props: { scope: TrainingTypeScope }): JSX.Element {
    return (
        <tr class={Css.tableRow}>
            <td class={Css.textCell}>{props.scope.label}</td>
            <td class={Css.numberCell}>{props.scope.workoutsLabel}</td>
            <td class={Css.numberCell}>{props.scope.usersLabel}</td>
            <td class={Css.numberCell}>{props.scope.perUserLabel}</td>
            <td class={Css.numberCell}>{props.scope.durationLabel}</td>
            <td class={Css.numberCell}>{props.scope.distanceLabel}</td>
            <td class={Css.numberCell}>{props.scope.heartRateLabel}</td>
        </tr>
    )
}

// ========== ATIVIDADE FISICA ==========

function Physical(props: { scope: UserBaseScope }): JSX.Element {
    const labels = () => props.scope.physicalSeries.map((point) => point.label)

    return (
        <div class={Css.group}>
            <Kpis items={props.scope.physicalKpis} columns={4} />

            <ChartPanel
                title="Passos"
                description="Total do período e média por colaborador que registrou dados."
                onExport={() => void props.scope.onExportPhysical()}
            >
                <ComboChart
                    height={260}
                    labels={labels()}
                    format={formatCount}
                    series={[
                        {
                            name: 'Passos totais',
                            kind: 'bar',
                            color: COLORS.total,
                            values: props.scope.physicalSeries.map((point) => point.steps)
                        },
                        {
                            name: 'Média por colaborador',
                            kind: 'line',
                            color: COLORS.average,
                            values: props.scope.physicalSeries.map((point) => point.averageSteps)
                        }
                    ]}
                />
            </ChartPanel>

            <ChartPanel
                title="Quilômetros percorridos"
                description="Total do período e média por colaborador que registrou dados."
                onExport={() => void props.scope.onExportPhysical()}
            >
                <ComboChart
                    height={260}
                    labels={labels()}
                    format={formatDecimal}
                    series={[
                        {
                            name: 'Km totais',
                            kind: 'bar',
                            color: COLORS.distance,
                            values: props.scope.physicalSeries.map((point) => point.distanceKm)
                        },
                        {
                            name: 'Média por colaborador',
                            kind: 'line',
                            color: COLORS.average,
                            values: props.scope.physicalSeries.map((point) => point.averageDistanceKm)
                        }
                    ]}
                />
            </ChartPanel>
        </div>
    )
}

// ========== VESTIVEIS ==========

/**
 * A bateria da pulseira e do anel: quantos estao com carga baixa e quanto uma carga dura.
 *
 * A tabela e a mesma forma da de modalidades, dentro de um `ChartPanel` porque precisa da
 * descricao e do botao de exportar.
 */
function Devices(props: { scope: UserBaseScope }): JSX.Element {
    return (
        <div class={Css.group}>
            <Kpis items={props.scope.deviceKpis} columns={4} />

            <ChartPanel
                title="Bateria por vestível"
                description="Carga na última leitura de cada pessoa e quanto ela cai por dia de uso. A queda soma os trechos de descarga entre leituras seguidas; recarga e intervalos de mais de 48 h ficam fora."
                onExport={() => void props.scope.onExportDevices()}
            >
                <div class={Css.scroller}>
                    <table class={Css.table}>
                        <thead>
                            <tr class={Css.tableHead}>
                                <th class={Css.textColumn}>Vestível</th>
                                <th class={Css.numberColumn}>Pessoas</th>
                                <th class={Css.numberColumn}>Leituras</th>
                                <th class={Css.numberColumn}>Até 20%</th>
                                <th class={Css.numberColumn}>21 a 50%</th>
                                <th class={Css.numberColumn}>Acima de 50%</th>
                                <th class={Css.numberColumn}>Queda por dia</th>
                                <th class={Css.numberColumn}>Uma carga dura</th>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={props.scope.deviceSources}>{(source) => <DeviceRow scope={source} />}</For>
                        </tbody>
                    </table>
                </div>

                <Show when={props.scope.deviceSources.length === 0}>
                    <p class={Css.noRow}>Nenhuma leitura de bateria no período.</p>
                </Show>
            </ChartPanel>
        </div>
    )
}

function DeviceRow(props: { scope: DeviceSourceScope }): JSX.Element {
    return (
        <tr class={Css.tableRow}>
            <td class={Css.textCell}>{props.scope.label}</td>
            <td class={Css.numberCell}>{props.scope.usersLabel}</td>
            <td class={Css.numberCell}>{props.scope.readingsLabel}</td>
            <td class={Css.numberCell}>{props.scope.lowLabel}</td>
            <td class={Css.numberCell}>{props.scope.midLabel}</td>
            <td class={Css.numberCell}>{props.scope.highLabel}</td>
            <td class={Css.numberCell} title={props.scope.dropHint}>
                {props.scope.dropLabel}
            </td>
            <td class={Css.numberCell}>{props.scope.daysPerChargeLabel}</td>
        </tr>
    )
}

// ========== AS PECAS COMUNS DESTA TELA ==========

/**
 * O titulo de um grupo de indicadores, com o que ele promete embaixo.
 *
 * Existe para a tela DIZER a divisao que a barra de filtros passou a fazer: de um lado o
 * retrato na data de referencia, do outro o fluxo entre as duas datas. Sem o titulo, os
 * cartoes voltariam a ser uma grade unica com duas naturezas dentro.
 */
function SectionLabel(props: { title: string; hint: string }): JSX.Element {
    return (
        <div class={Css.sectionLabel}>
            <h3 class={Css.sectionTitle}>{props.title}</h3>
            <p class={Css.sectionHint}>{props.hint}</p>
        </div>
    )
}

function Kpis(props: { items: KpiScope[]; columns: 4 | 6 }): JSX.Element {
    return (
        <div class={Css.cards} classList={{ [Css.sixUp]: props.columns === 6, [Css.fourUp]: props.columns === 4 }}>
            <For each={props.items}>
                {(kpi) => (
                    /*
                        O cartao que LEVA a algum lugar e um `<button>`; o que nao leva
                        continua sendo um `<div>`.
                        
                        Nao e purismo: um `div` com `onClick` nao recebe foco, nao
                        responde ao Enter e nao e anunciado como acionavel - e metade
                        destes cartoes nao leva a lugar nenhum, entao o elemento e a
                        unica coisa que separa os dois casos para quem navega por
                        teclado ou por leitor de tela.
                    */
                    <Dynamic
                        component={kpi.onOpen ? 'button' : 'div'}
                        type={kpi.onOpen ? 'button' : undefined}
                        class={Css.card}
                        classList={{ [Css.cardOpens]: !!kpi.onOpen }}
                        // Com caminho, o `title` diz para onde se vai; sem ele, continua
                        // explicando de onde sai o numero.
                        title={kpi.openHint ? `${kpi.help ?? ''}\n${kpi.openHint}`.trim() : kpi.help}
                        onClick={kpi.onOpen ? () => void kpi.onOpen?.() : undefined}
                    >
                        <span class={`${Css.cardIcon} ${ACCENTS[kpi.accent] ?? ACCENTS.indigo}`}>
                            <Icon name={kpi.icon} size={18} />
                        </span>

                        <div class={Css.cardTexts}>
                            {/*
                                O rotulo QUEBRA em vez de truncar: "Pico de conectados
                                no período" nao cabe num cartao de um sexto da largura,
                                e truncado ele vira "Pico de con…", que nao diz nada.
                            */}
                            <p class={Css.cardLabel}>{kpi.label}</p>
                            <p class={Css.cardValue}>{kpi.value}</p>
                            <Show when={kpi.subtitle}>{(subtitle) => <p class={Css.cardNote}>{subtitle()}</p>}</Show>
                        </div>
                    </Dynamic>
                )}
            </For>
        </div>
    )
}

/** Um painel de grafico, com a descricao e o botao de exportar. */
function ChartPanel(props: {
    title: string
    description?: string
    onExport?: () => void
    class?: string
    children: JSX.Element
}): JSX.Element {
    return (
        <section class={`${Css.chartCard} ${props.class ?? ''}`}>
            <header class={Css.chartCardTop}>
                <div class={Css.chartCardTexts}>
                    <h3 class={Css.chartCardTitle}>{props.title}</h3>
                    <Show when={props.description}>
                        {(description) => <p class={Css.chartCardNote}>{description()}</p>}
                    </Show>
                </div>

                <Show when={props.onExport}>
                    {(onExport) => (
                        <button type="button" onClick={onExport()} class={Css.export}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.75"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class={Css.icon}
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                            </svg>
                            Exportar CSV
                        </button>
                    )}
                </Show>
            </header>

            {props.children}
        </section>
    )
}
